import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Box,
} from '@mui/material';
import { Download } from '@mui/icons-material';

interface Props {
  content: string;
}

// Helper functions for table detection and parsing
function isTable(lines: string[]) {
  if (lines.length < 2) return false;
  const header = lines[0];
  const separator = lines[1];
  return /^\s*\|.*\|\s*$/.test(header) && /^\s*\|?(\s*:?-+:?\s*\|)+\s*$/.test(separator);
}

function parseTable(lines: string[]) {
  return lines.map((line) =>
    line
      .trim()
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((cell) => cell.trim())
  );
}

const MathRenderer: React.FC<Props> = ({ content }) => {

  // const normalizedContent = content.replace(
  //   /\$\s*([\s\S]*?)\s*\$/g,
  //   (_, math) => `$${math.replace(/\s+/g, '')}$`
  // );
  const normalizedContent = content
    // 🛡️ Protect block math $$ ... $$
    .replace(/\$\$([\s\S]*?)\$\$/g, (_, math) => `@@BLOCK@@${math}@@BLOCK@@`)
    .replace(/\$([^\n$]+)\$/g, (_, math) => `$${math.trim()}$`)

    // .replace(/\$([^\n$]+)\$/g, (_, math) => `$${math.replace(/\s+/g, '')}$`) 
    .replace(/@@BLOCK@@([\s\S]*?)@@BLOCK@@/g, (_, math) => `$$\n${math.trim()}\n$$`);


  const lines = normalizedContent.split('\n');
  const elements: React.ReactNode[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Empty line
    if (!trimmedLine) {
      elements.push(<div key={`empty-${i}`} style={{ height: '0.5em' }} />);
      i++;
      continue;
    }

    // 🔹 TABLE DETECTION
    if (i + 1 < lines.length) {
      const maybeTableLines = lines.slice(i, Math.min(i + 3, lines.length));
      if (isTable(maybeTableLines)) {
        // Collect all table lines
        const tableLines: string[] = [];
        let k = i;
        while (k < lines.length && lines[k].includes('|') && lines[k].trim() !== '') {
          tableLines.push(lines[k]);
          k++;
        }

        const rows = parseTable(tableLines);

        elements.push(
          <Box key={`table-${i}`} sx={{ my: 2 }}>
            <Tooltip title="Download Table as CSV">
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  const csvData = rows.map((row) => row.join(',')).join('\n');
                  const blob = new Blob([csvData], { type: 'text/csv' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = 'table.csv';
                  link.click();
                }}
                sx={{ mb: 1 }}
              >
                <Download sx={{ mr: 0.5, fontSize: 16 }} /> Download CSV
              </Button>
            </Tooltip>
            <Table
              size="small"
              aria-label="markdown table"
              sx={{
                border: '1px solid #ccc',
                bgcolor: '#f8f9fa',
                borderRadius: 1,
                '& .MuiTableCell-root': {
                  fontSize: { xs: '11px', sm: '13px' },
                  padding: { xs: '6px 8px', sm: '8px 12px' },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {rows[0].map((cell, idx) => (
                    <TableCell
                      key={`head-${idx}`}
                      sx={{
                        fontWeight: 'bold',
                        bgcolor: '#667eea',
                        color: '#fff',
                        borderRight: idx < rows[0].length - 1 ? '1px solid #fff' : 'none',
                      }}
                    >
                      {renderCellContent(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(2).map((row, ridx) => (
                  <TableRow
                    key={`row-${ridx}`}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: '#ffffff' },
                      '&:nth-of-type(even)': { bgcolor: '#f8f9fa' },
                      '&:hover': { bgcolor: '#e8eaf0' },
                    }}
                  >
                    {row.map((cell, cidx) => (
                      <TableCell
                        key={`cell-${ridx}-${cidx}`}
                        sx={{
                          borderRight: cidx < row.length - 1 ? '1px solid #e0e0e0' : 'none',
                        }}
                      >
                        {renderCellContent(cell)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        );

        i = k;
        continue;
      }
    }

    // 🔹 BLOCK MATH $$ ... $$
    // if (trimmedLine.startsWith('$$') && trimmedLine.endsWith('$$')) {
    //   const formula = trimmedLine.slice(2, -2).trim();
    //   elements.push(
    //     <div
    //       key={`block-math-${i}`}
    //       style={{
    //         margin: '1em 0',
    //         padding: '8px',
    //         backgroundColor: '#f8f9fa',
    //         borderRadius: '8px',
    //         borderLeft: '4px solid #667eea',
    //       }}
    //     >
    //       <BlockMath math={formula} />
    //     </div>
    //   );
    //   i++;
    //   continue;
    // }
    // 🔹 MULTILINE BLOCK MATH $$ ... $$
    if (trimmedLine === '$$') {
      let formulaLines: string[] = [];
      let j = i + 1;

      while (j < lines.length && lines[j].trim() !== '$$') {
        formulaLines.push(lines[j]);
        j++;
      }

      if (j < lines.length) {
        const formula = formulaLines.join('\n').trim();

        elements.push(
          <Box
            key={`block-math-${i}`}
            sx={{
              my: 2,
              p: 1,
              bgcolor: '#f8f9fa',
              borderRadius: 2,
              borderLeft: '4px solid #667eea',
              overflowX: { xs: 'auto', sm: 'visible' },
            }}
          >
            <BlockMath math={formula} />
          </Box>
        );

        i = j + 1;
        continue;
      }
    }

    // 🔹 HEADINGS
    if (
      trimmedLine.endsWith(':') &&
      !trimmedLine.startsWith('•') &&
      !/^\s*[-–—]\s+/.test(trimmedLine)
    ) {
      elements.push(
        <div
          key={`heading-${i}`}
          style={{
            // fontWeight: 700,
            fontSize: '12px',
            margin: '1em 0 0.5em',
            color: '#000000',
            borderBottom: '2px solid #e5e7eb',
            paddingBottom: '4px',
          }}
        >
          {renderTextWithLinks(trimmedLine)}
        </div>
      );
      i++;
      continue;
    }

    // 🔹 BULLETS
    if (trimmedLine.startsWith('•')) {
      elements.push(
        <div
          key={`bullet-${i}`}
          style={{
            margin: '6px 0',
            lineHeight: 1.5,
            paddingLeft: '8px',
          }}
        >
          {renderTextWithLinks(trimmedLine.substring(1).trim())}
        </div>
      );
      i++;
      continue;
    }

    // 🔹 SUB-BULLETS
    if (/^\s*[-–—]\s+/.test(trimmedLine)) {
      elements.push(
        <div
          key={`sub-bullet-${i}`}
          style={{
            margin: '4px 0 4px 20px',
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <span style={{ marginRight: '6px' }}>🔹</span>
          <span>
            {renderTextWithLinks(trimmedLine.replace(/^\s*[-–—]\s+/, ''))}
          </span>
        </div>
      );
      i++;
      continue;
    }

    // 🔹 NUMBERED LIST
    if (/^\d+\./.test(trimmedLine)) {
      elements.push(
        <div
          key={`numbered-${i}`}
          style={{
            margin: '4px 0 4px 20px',
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          {renderTextWithLinks(trimmedLine)}
        </div>
      );
      i++;
      continue;
    }

    // 🔹 NORMAL TEXT
    elements.push(
      <div
        key={`text-${i}`}
        style={{
          margin: '4px 0',
          lineHeight: 1.5,
        }}
      >
        {renderTextWithLinks(trimmedLine)}
      </div>
    );
    i++;
  }

  return (
    <div
      style={{
        fontSize: '13px',
        color: '#1a1a1a',
        maxWidth: '100%',
        position: 'relative',
        whiteSpace: 'pre-wrap',
      }}
    >
      {elements}
    </div>
  );
};

// Helper to render cell content with math support
const renderCellContent = (text: string) => {
  const parts = renderTextWithLinks(text);
  return parts;
};


// 🔹 INLINE MATH + LINK RENDERER (HARDENED)
const renderTextWithLinks = (text: string) => {
  const elements: React.ReactNode[] = [];
const regex = /(https:\/\/[^\s]+)|(\$[^$]+\$|\\\([\s\S]*?\\\))|(\*\*(.+?)\*\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add normal text before match
    if (match.index > lastIndex) {
      elements.push(
        <span key={lastIndex}>{text.slice(lastIndex, match.index)}</span>
      );
    }

    const part = match[0];

    // 🔗 URL
    if (match[1]) {
      elements.push(
        <a
          key={match.index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          // style={{ color: '#667eea', textDecoration: 'none' }}
        >
          {part}
        </a>
      );
    }
    // 🔢 INLINE MATH
    else if (match[2]) {
      const math = part.startsWith('$')
        ? part.slice(1, -1)
        : part.slice(2, -2);

      elements.push(
        <span
          key={match.index}
          style={{
            display: 'inline-block',
            padding: '2px 4px',
            // backgroundColor: '#f0f4ff',
            borderRadius: '4px',
            margin: '0 1px',
          }}
        >
          <InlineMath math={math} />
        </span>
      );
    }
 else if (match[3]) {
      elements.push(
        <strong key={`bold-${match.index}`} style={{ fontWeight: 700 }}>
          {match[4]}
        </strong>
      );
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    elements.push(<span key={lastIndex}>{text.slice(lastIndex)}</span>);
  }

  return elements;
};


export default MathRenderer;

